/**
 * The one call the transcript makes for the two block kinds this folder owns.
 *
 * `DeckThread` walks a `TurnBlock[]` and switches on `kind`; `prose`, `artifact`, `question` and
 * `task` belong to other tracks. This takes `activity` and `thinking` off that switch and returns
 * `null` for everything else, so the thread's cases stay one line each and nothing has to know which
 * of the twenty-two artboards a given block turns into.
 */
import type { ChatTheme } from '~/design-system/components/v1_5/Chat';
import type { TurnBlock } from '../turnModel';
import { ActivityCard } from './ActivityCard';
import { ThinkingBlock } from './ThinkingBlock';
import { GoalWidgetUI } from '~/design-system/components/PlanningChat/ui/GoalWidgetUI';
import { ArtifactCard, ActivityGlyph } from '~/design-system/components/v1_5/Chat';
import resourceStyles from './ResourceWidget.module.css';

export interface TurnActivityProps {
  block: TurnBlock;
  /** The live turn's clock, `live.elapsedSec`. Only a `thinking` block that is still live reads it. */
  elapsedSec?: number;
  theme: ChatTheme;
  onOpenResource?: (key: string) => void;
}

export function TurnActivity({ block, elapsedSec, theme, onOpenResource }: TurnActivityProps) {
  if (block.kind === 'activity' && block.tool === 'goal' && block.lifecycle !== 'failed') {
    return <div className={resourceStyles.root}><GoalWidgetUI status={block.lifecycle === 'running' || block.lifecycle === 'updating' ? 'writing' : 'complete'}
      goalText={block.body.kind === 'explanation' ? block.body.text : undefined} hideFooter
      onNavigateToDocument={onOpenResource ? () => onOpenResource('goal') : undefined} /></div>;
  }
  if (block.kind === 'activity' && block.tool === 'document' && block.resourceKey) {
    return <ArtifactCard title={block.resourceTitle || block.label} theme={theme}
      meta={block.lifecycle === 'running' ? 'Writing document' : 'Document'}
      thumbnail={<ActivityGlyph tool="document" size={16} theme={theme} />}
      busy={block.lifecycle === 'running' || block.lifecycle === 'updating'}
      onOpen={onOpenResource ? () => onOpenResource(block.resourceKey!) : undefined} />;
  }
  if (block.kind === 'activity') return <ActivityCard block={block} theme={theme} />;
  if (block.kind === 'thinking') {
    return <ThinkingBlock block={block} elapsedSec={elapsedSec} theme={theme} />;
  }
  return null;
}

/**
 * Does this turn already draw a working mark of its own?
 *
 * `LiveThinkingFooter` shows a bare `ThinkingIndicator` while a turn is in flight, and must not show
 * a second one when the turn's last block is already a live `thinking` block drawing the same row.
 * Two dot matrices breathing 12px apart is the failure this exists to prevent.
 *
 * Written as a predicate over the blocks rather than read off `live.thinking` because the two can
 * disagree for one paint: `TurnBuilder.thinkingLive` goes false the instant `sealThinking` runs,
 * while the block it just settled is still in the array and still the last thing on screen.
 */
export function drawsOwnIndicator(blocks: TurnBlock[]): boolean {
  return blocks.some((block) => block.kind === 'thinking' && block.live);
}

/**
 * Is the answer being written right now?
 *
 * The other half of the footer's gate. A sentence appearing a word at a time is already the liveness
 * signal, and a status row under it is a second thing claiming to be the news — so the footer stands
 * down the moment prose becomes the tail and comes back when anything else does.
 *
 * The LAST block, not "any prose anywhere": a turn is a sequence, and one that wrote a paragraph and
 * then went off to run a tool is working again. `TurnBuilder` pushes every non-prose event after the
 * paragraph it interrupted, so the tail is the only thing that has to be read.
 *
 * Empty prose does not count. `sealProse` drops a blank paragraph, but a boundary that arrives before
 * the first chunk leaves one open and unwritten for a paint or two, and yielding to nothing is how
 * the dead air comes back.
 */
export function isAnswering(blocks: TurnBlock[]): boolean {
  const last = blocks[blocks.length - 1];
  return last?.kind === 'prose' && last.text.trim().length > 0;
}

/** A real activity widget owns the live status while its operation is still moving. */
export function drawsLiveActivity(blocks: TurnBlock[]): boolean {
  return blocks.some(
    (block) =>
      block.kind === 'activity' &&
      (block.lifecycle === 'running' || block.lifecycle === 'updating'),
  );
}

/**
 * The blocks worth keeping once a turn has settled.
 *
 * `transient` is machinery — skill loads, handoffs, the one-line `action` narration — and
 * `wireVocabulary.ts` marks it because four skill loads in a row stack four identical lines that
 * tell the reader nothing. They are real while the turn is running (the agent IS doing something)
 * and noise in the record afterwards.
 */
export function settledBlocks(blocks: TurnBlock[]): TurnBlock[] {
  return blocks.filter((block) => !(block.kind === 'activity' && block.transient));
}
